import type {
  AdapterStatus,
  AppNotification,
  AppState,
  CheckIn,
  FeedPost,
  ItineraryItem,
  PassportStamp,
  Preferences,
  SupportCase,
  TicketConnection,
  UserProfile,
} from '../domain/models'
import { toggleId, uniqueIds } from '../domain/helpers'
import { createAdapterStatuses } from './initialState'

export type AppReducerAction =
  | { type: 'replace-state'; state: AppState }
  | { type: 'set-email-next-step'; pending: boolean }
  | { type: 'set-adapter-status'; adapter: keyof AppState['adapterStatuses']; status: AdapterStatus }
  | { type: 'update-profile'; user: UserProfile }
  | { type: 'select-show'; showId: string }
  | { type: 'toggle-saved-show'; showId: string }
  | { type: 'ticket-connected'; ticket: TicketConnection; itineraryItem: ItineraryItem; notification: AppNotification }
  | { type: 'ticket-unlinked'; ticketId: string }
  | { type: 'itinerary-added'; item: ItineraryItem }
  | { type: 'itinerary-moved'; itemId: string; targetItemId: string }
  | { type: 'itinerary-toggled'; itemId: string }
  | { type: 'itinerary-removed'; itemId: string }
  | { type: 'post-created'; post: FeedPost }
  | { type: 'post-liked'; postId: string }
  | { type: 'post-saved'; postId: string }
  | { type: 'post-shared'; postId: string }
  | { type: 'post-reported'; postId: string }
  | { type: 'post-report-undone'; postId: string }
  | { type: 'checked-in'; checkIn: CheckIn; stamp: PassportStamp; notification: AppNotification }
  | { type: 'notification-read'; notificationId: string; readAt: string }
  | { type: 'notifications-read-all'; readAt: string }
  | { type: 'preferences-updated'; patch: Partial<Preferences> }
  | { type: 'support-created'; supportCase: SupportCase }
  | { type: 'online-changed'; online: boolean }

export const appReducer = (state: AppState, action: AppReducerAction): AppState => {
  switch (action.type) {
    case 'replace-state':
      return action.state
    case 'set-email-next-step':
      return { ...state, emailNextStepPending: action.pending }
    case 'set-adapter-status':
      return {
        ...state,
        adapterStatuses: { ...state.adapterStatuses, [action.adapter]: action.status },
      }
    case 'update-profile':
      return { ...state, user: action.user }
    case 'select-show':
      return { ...state, selectedShowId: action.showId }
    case 'toggle-saved-show':
      return { ...state, savedShowIds: toggleId(state.savedShowIds, action.showId) }
    case 'ticket-connected': {
      const hasTicket = state.tickets.some((ticket) => ticket.id === action.ticket.id)
      const hasItineraryItem = state.itinerary.some((item) => item.id === action.itineraryItem.id)
      return {
        ...state,
        selectedShowId: action.ticket.showId,
        savedShowIds: uniqueIds([...state.savedShowIds, action.ticket.showId]),
        tickets: hasTicket ? state.tickets : [...state.tickets, action.ticket],
        itinerary: hasItineraryItem ? state.itinerary : [...state.itinerary, action.itineraryItem],
        notifications: state.notifications.some((item) => item.id === action.notification.id)
          ? state.notifications
          : [action.notification, ...state.notifications],
      }
    }
    case 'ticket-unlinked':
      return {
        ...state,
        tickets: state.tickets.filter((ticket) => ticket.id !== action.ticketId),
        itinerary: state.itinerary.filter((item) => item.id !== `itinerary-${action.ticketId}`),
      }
    case 'itinerary-added':
      return state.itinerary.some((item) => item.id === action.item.id)
        ? state
        : { ...state, itinerary: [...state.itinerary, action.item] }
    case 'itinerary-moved': {
      const itemIndex = state.itinerary.findIndex((item) => item.id === action.itemId)
      const targetIndex = state.itinerary.findIndex((item) => item.id === action.targetItemId)
      if (itemIndex < 0 || targetIndex < 0 || itemIndex === targetIndex) return state
      const itinerary = [...state.itinerary]
      const item = itinerary[itemIndex]
      const target = itinerary[targetIndex]
      if (!item || !target) return state
      itinerary[itemIndex] = target
      itinerary[targetIndex] = item
      return { ...state, itinerary }
    }
    case 'itinerary-toggled':
      return {
        ...state,
        itinerary: state.itinerary.map((item) => item.id === action.itemId
          ? { ...item, completed: !item.completed }
          : item),
      }
    case 'itinerary-removed':
      return { ...state, itinerary: state.itinerary.filter((item) => item.id !== action.itemId) }
    case 'post-created':
      return state.feedPosts.some((post) => post.id === action.post.id)
        ? state
        : { ...state, feedPosts: [...state.feedPosts, action.post] }
    case 'post-liked':
      return { ...state, likedPostIds: toggleId(state.likedPostIds, action.postId) }
    case 'post-saved':
      return { ...state, savedPostIds: toggleId(state.savedPostIds, action.postId) }
    case 'post-shared':
      return { ...state, sharedPostIds: uniqueIds([...state.sharedPostIds, action.postId]) }
    case 'post-reported':
      return { ...state, reportedPostIds: uniqueIds([...state.reportedPostIds, action.postId]) }
    case 'post-report-undone':
      return { ...state, reportedPostIds: state.reportedPostIds.filter((id) => id !== action.postId) }
    case 'checked-in': {
      const hasCheckIn = state.checkIns.some((item) => item.showId === action.checkIn.showId)
      const hasStamp = state.passportStamps.some((stamp) => stamp.showId === action.stamp.showId)
      return {
        ...state,
        checkIns: hasCheckIn ? state.checkIns : [...state.checkIns, action.checkIn],
        passportStamps: hasStamp ? state.passportStamps : [...state.passportStamps, action.stamp],
        itinerary: state.itinerary.map((item) => item.showId === action.checkIn.showId && item.kind === 'ticket'
          ? { ...item, completed: true }
          : item),
        notifications: state.notifications.some((item) => item.id === action.notification.id)
          ? state.notifications
          : [action.notification, ...state.notifications],
      }
    }
    case 'notification-read':
      return {
        ...state,
        notifications: state.notifications.map((notification) => notification.id === action.notificationId
          ? { ...notification, readAt: notification.readAt ?? action.readAt }
          : notification),
      }
    case 'notifications-read-all':
      return {
        ...state,
        notifications: state.notifications.map((notification) => ({
          ...notification,
          readAt: notification.readAt ?? action.readAt,
        })),
      }
    case 'preferences-updated':
      return { ...state, preferences: { ...state.preferences, ...action.patch } }
    case 'support-created':
      return { ...state, supportCases: [...state.supportCases, action.supportCase] }
    case 'online-changed':
      return {
        ...state,
        online: action.online,
        adapterStatuses: createAdapterStatuses(action.online),
      }
    default:
      return state
  }
}
